// The shared scoped-search controls (spec section 5, "Search on a
// surface"): one bar on every searchable surface, the pill plus the source
// select, same labels and same follows default on Discover, Feed, and
// Crossfire. The scope sits beside the results and resets on every screen
// entry (store.nav), so Everyone is always a fresh, explicit choice.
import { useEffect, type ReactNode } from "react";
import type { TabsDef } from "./descriptor.js";
import type { ExpertLite } from "../state/api.js";
import { useStore, type SearchScope } from "../state/store.js";

export function SearchBox({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string;
  onChange: (q: string) => void;
  placeholder: string;
  label: string;
}) {
  return (
    <div className="bigsearch">
      <span className="bsic">⌕</span>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
      />
      {value !== "" && (
        <button className="bsclear" aria-label="Clear search" onClick={() => onChange("")}>
          ✕
        </button>
      )}
    </div>
  );
}

// The scope is the search view's filter bar (31 Jul 2026, Rob), returned as
// a tabs descriptor so it is literally the strip every other surface uses,
// in the slot that strip occupies, rather than a look-alike. Chips came
// before this and read as additive filters, which a scope is not.
//
// The source is the strip's third pane, not a control parked beside it
// (31 Jul 2026, Rob, correcting the first pass): three answers to one
// question, same width, same shape, one lit at a time. It is a select all
// along rather than a button that reveals one, so choosing a person is one
// gesture; it reads "Specific person" until someone is chosen and their
// name after. Naming a person is a scope, so neither scope pane stays lit
// while one is named, and picking a scope pane hands the person back.
export function scopeTabs(
  scope: SearchScope,
  onPick: (s: SearchScope) => void,
  source?: { value: string | null; onChange: (id: string | null) => void; kind: SourceKind },
): TabsDef {
  return {
    items: [
      ["follows", "People you follow"],
      ["all", "Everyone"],
    ],
    // No counts: there is no honest number for "Everyone" until it is run.
    counts: {},
    active: source?.value ? "" : scope,
    onPick: (id) => onPick(id as SearchScope),
    equal: true,
    extra: source ? (
      <SourcePane value={source.value} onChange={source.onChange} kind={source.kind} />
    ) : undefined,
  };
}

// The roster, split by whether you follow them. Shared by both pickers so
// the two lists can never drift apart.
//
// Anyone the surface would list nothing for is left out (31 Jul 2026, Rob):
// a source that returns an empty screen is not a filter, it is a trap, and
// the roster is 150 people deep. The two surfaces count different things,
// so they are two counts: someone with articles and no practices yet
// belongs in the feed's picker and not Discover's.
export type SourceKind = "practices" | "feed";

function sourceOptions(kind: SourceKind): ReactNode {
  const roster = useStore((s) => s.experts) ?? [];
  const followSet = new Set(useStore((s) => s.appState?.follows) ?? []);
  const has = (e: ExpertLite) => (kind === "feed" ? e.feedCount > 0 : e.cardCount > 0);
  const pool = roster.filter(has);
  const followed = pool.filter((e) => followSet.has(e.id));
  const others = pool.filter((e) => !followSet.has(e.id));
  return (
    <>
      {followed.length > 0 && (
        <optgroup label="Following">
          {followed.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}
            </option>
          ))}
        </optgroup>
      )}
      {others.length > 0 && (
        <optgroup label="Everyone else">
          {others.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}
            </option>
          ))}
        </optgroup>
      )}
    </>
  );
}

// The strip's third pane. Dressed as its neighbours down to the lit state,
// which it takes from having a person chosen rather than from aria-selected
// (not a thing a select can carry).
function SourcePane({
  value,
  onChange,
  kind,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  kind: SourceKind;
}) {
  return (
    <select
      className="segsel"
      data-on={value ? "true" : "false"}
      aria-label="One person"
      value={value ?? ""}
      onChange={(ev) => onChange(ev.target.value || null)}
    >
      <option value="">Specific person</option>
      {sourceOptions(kind)}
    </select>
  );
}

// One source picker on every surface that has sources: everyone you follow
// by default, then anyone at all, because nothing here is gated. Following
// only decides what lands by default. This is the toolbar's, for the view
// with no query in it; the search view uses the pane above.
export function ExpertSelect({
  value,
  onChange,
  kind,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  kind: SourceKind;
}) {
  return (
    <span className="selwrap">
      <select
        aria-label="Whose work"
        value={value ?? ""}
        onChange={(ev) => onChange(ev.target.value || null)}
      >
        <option value="">Everyone you follow</option>
        {sourceOptions(kind)}
      </select>
    </span>
  );
}

// Debounces a server-backed search as the query changes. Lives here so the
// screens stay pure functions; the effect belongs to the mounted component.
// Fires on every change, empty included: the run action decides whether
// that means fetch or clear (an emptied query with a source still chosen
// must refresh, not stall).
export function useDebouncedSearch(q: string, run: () => void) {
  // run is a stable store action; q is the only real dependency.
  useEffect(() => {
    const t = setTimeout(run, 250);
    return () => clearTimeout(t);
  }, [q]);
}
